import { useCallback, useRef, useSyncExternalStore } from 'react';

export type UseLocalStorageOptions<Value> = {
  initialValue?: Value;
  /*
   * storageSync option syncs value between browser tabs
   * it works on rootKey (microfrontend) level
   * it means if there is a useLocalStorage hook with enabled storageSync
   * then synchronization between tabs would work for every hook with the same rootKey
   */
  storageSync?: boolean;
};

const defaultOptions = {
  storageSync: false,
};

const noop = () => {};

export function useLocalStorage<T = undefined>(
  key: `@myn/${string}.${string}` = `@myn/placeholder.placeholder`,
  { initialValue, storageSync }: UseLocalStorageOptions<T> = defaultOptions,
): [T, (value: T) => void] {
  const storageItem = useRef<{
    unparsedValue: string | null;
    parsedNestedValue: T | undefined;
  }>({
    unparsedValue: null,
    parsedNestedValue: initialValue,
  });

  const [rootKey, ...restKeys]: string[] = key
    .split('.')
    .filter((k) => k.trim() !== '');
  const lastKey = restKeys[restKeys.length - 1];

  const subscribeToLocalStorageChange = useCallback(
    (onStoreChange: () => void) => {
      addCallback(rootKey, onStoreChange);

      const onLocalStorageChange = (event: StorageEvent): void => {
        if (event.key === rootKey) {
          triggerCallbacks(event.key as string);
        }
      };

      if (storageSync) {
        globalThis.addEventListener('storage', onLocalStorageChange);

        return () => {
          globalThis.removeEventListener('storage', onLocalStorageChange);
        };
      }

      return noop;
    },
    [rootKey, storageSync],
  );

  const getStoreSnapshot = (): T => {
    try {
      const storedValue: string | null = localStorage.getItem(rootKey);

      if (storedValue !== storageItem.current.unparsedValue) {
        const parsedValue: any = storedValue
          ? JSON.parse(storedValue)
          : initialValue;

        // Try to get nested value
        const nestedValue = restKeys.reduce(
          (acc, currentKey) => acc[currentKey],
          parsedValue,
        );

        storageItem.current.parsedNestedValue =
          nestedValue !== undefined ? nestedValue : initialValue;
      }

      storageItem.current.unparsedValue = storedValue;
    } catch {}

    return storageItem.current.parsedNestedValue as T;
  };

  const value = useSyncExternalStore<T>(
    subscribeToLocalStorageChange,
    getStoreSnapshot,
  );

  const setValue = (newValue: T) => {
    try {
      const storedValue: any = JSON.parse(
        localStorage.getItem(rootKey) || '{}',
      );

      const nestedStoredObject = restKeys
        .slice(0, -1)
        .reduce((obj, keyFragment) => {
          if (!obj[keyFragment]) {
            obj[keyFragment] = {};
          }
          obj = obj[keyFragment];

          return obj;
        }, storedValue);

      nestedStoredObject[lastKey] = newValue;

      localStorage.setItem(rootKey, JSON.stringify(storedValue));
      triggerCallbacks(rootKey);
    } catch (error) {
      console.error(error);
    }
  };

  return [value, setValue];
}

type RootKey = string;
type OnStoreChange = () => void;

const callbacksMap = new Map<RootKey, Set<OnStoreChange>>();

function addCallback(rootKey: string, callback: OnStoreChange): void {
  if (!callbacksMap.has(rootKey)) {
    callbacksMap.set(rootKey, new Set<OnStoreChange>());
  }

  const callbacks = callbacksMap.get(rootKey) as Set<OnStoreChange>;
  callbacks.add(callback);
}

function triggerCallbacks(rootKey: string): void {
  const callbacks = callbacksMap.get(rootKey) || new Set<OnStoreChange>();
  for (const callback of [...callbacks]) {
    callback();
  }
}
